import React, { useMemo, useState } from "react";
import { format, isValid, parseISO } from "date-fns";
import { GlobalTableActionButton } from "../../../../global/GlobalTableActionButton";
import GlobalBadge from "../../../../global/GlobalBadge";
import GlobalDirectoryFiltersSection from "../../../../global/directory/GlobalDirectoryFiltersSection";
import GlobalDirectoryGridSection from "../../../../global/directory/GlobalDirectoryGridSection";
import {
  GlobalDirectoryShell,
  GlobalDirectoryColumnPicker,
  GlobalDirectoryExportMenu,
  GlobalDirectoryPaginationSection,
} from "../../../../global/directory";
import { filterActiveMasterRows } from "../../../memberships/utils/masterLookupUtils";
import { printChequeDetails } from "../../utils/printChequeDetails";

export const CHEQUE_DIRECTORY_COLUMN_VISIBILITY_KEY =
  "girinestham.chequeDirectory.columnVisibility";

export const CHEQUE_DIRECTORY_COLUMN_DEFS = [
  { id: "cheque_issue_code", label: "Issue code", defaultVisible: true },
  { id: "cheque_no", label: "Cheque no.", defaultVisible: true },
  { id: "cheque_date", label: "Date", defaultVisible: true },
  { id: "bank_name", label: "Bank", defaultVisible: true },
  { id: "payee_name", label: "Payee", defaultVisible: true },
  { id: "amount", label: "Amount", defaultVisible: true },
  { id: "status_name", label: "Status", defaultVisible: true },
  { id: "voucher_no", label: "Voucher", defaultVisible: false },
  { id: "type_name", label: "Type", defaultVisible: false },
  { id: "issued_by_name", label: "Issued by", defaultVisible: false },
];

const DIRECTORY_FILTER_COLUMNS = [
  { id: "bank", label: "Bank" },
  { id: "status", label: "Cheque status" },
  { id: "type", label: "Cheque type" },
  { id: "cheque_date", label: "Cheque date" },
];

function loadColumnVisibility() {
  try {
    const raw = localStorage.getItem(CHEQUE_DIRECTORY_COLUMN_VISIBILITY_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return null;
}

export function createInitialChequeColumnVisibility() {
  const saved = loadColumnVisibility();
  const initial = {};
  for (const c of CHEQUE_DIRECTORY_COLUMN_DEFS) {
    initial[c.id] = saved?.[c.id] ?? c.defaultVisible;
  }
  return initial;
}

function formatDateCell(value) {
  if (!value) return "—";
  const d = parseISO(String(value));
  return isValid(d) ? format(d, "PP") : String(value);
}

function formatAmount(value) {
  const n = Number(value);
  return Number.isFinite(n) ? `₹${n.toLocaleString("en-IN")}` : "—";
}

function chequeDateIso(value) {
  if (!value) return "";
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const d = parseISO(raw);
  return isValid(d) ? format(d, "yyyy-MM-dd") : "";
}

function chequeCellValue(colId, row) {
  switch (colId) {
    case "cheque_date":
      return formatDateCell(row.cheque_date);
    case "amount":
      return formatAmount(row.amount);
    default:
      return row[colId] ?? "—";
  }
}

function buildDirectoryValueOptions(column, masterData, cheques) {
  if (column === "bank") {
    return filterActiveMasterRows(masterData?.banks).map((b) => ({
      value: String(b.id),
      label: b.bank_name,
    }));
  }
  if (column === "status") {
    return filterActiveMasterRows(masterData?.statuses).map((s) => ({
      value: String(s.id),
      label: s.status_name,
    }));
  }
  if (column === "type") {
    return filterActiveMasterRows(masterData?.types).map((t) => ({
      value: String(t.id),
      label: t.type_name,
    }));
  }
  if (column === "cheque_date") {
    const seen = new Set();
    const opts = [];
    for (const row of cheques || []) {
      const iso = chequeDateIso(row.cheque_date);
      if (!iso || seen.has(iso)) continue;
      seen.add(iso);
      opts.push({ value: iso, label: formatDateCell(row.cheque_date) });
    }
    return opts.sort((a, b) => a.value.localeCompare(b.value));
  }
  return [];
}

function applyDirectoryColumnFilter(filters, column, value) {
  const cleared = {
    ...filters,
    bank_id: "",
    cheque_status_id: "",
    cheque_type_id: "",
    start_date: "",
    end_date: "",
    page: 1,
  };
  if (!column || !value) return cleared;
  if (column === "bank") return { ...cleared, bank_id: value };
  if (column === "status") return { ...cleared, cheque_status_id: value };
  if (column === "type") return { ...cleared, cheque_type_id: value };
  if (column === "cheque_date") {
    return { ...cleared, start_date: value, end_date: value };
  }
  return cleared;
}

function inferDirectoryFilterUi(filters) {
  if (filters.bank_id) return { column: "bank", value: String(filters.bank_id) };
  if (filters.cheque_status_id) {
    return { column: "status", value: String(filters.cheque_status_id) };
  }
  if (filters.cheque_type_id) {
    return { column: "type", value: String(filters.cheque_type_id) };
  }
  if (filters.start_date && filters.end_date && filters.start_date === filters.end_date) {
    return { column: "cheque_date", value: filters.start_date };
  }
  return { column: "", value: "" };
}

function ChequeDirectoryTable({
  cheques,
  loading,
  columnVisibility,
  onView,
  onEdit,
  onChangeStatus,
  isRowLocked,
}) {
  const visibleDataColumns = CHEQUE_DIRECTORY_COLUMN_DEFS.filter(
    (c) => columnVisibility[c.id],
  );
  const tableHeaders = [
    ...visibleDataColumns.map((c) => ({ key: c.id, label: c.label })),
    {
      key: "actions",
      label: "Actions",
      className: "ub-table__col-actions",
    },
  ];
  const showRows = !loading && cheques.length > 0;

  return (
    <GlobalDirectoryGridSection
      loading={loading}
      loadingMessage="Loading cheques…"
      headers={tableHeaders}
      isEmpty={!loading && cheques.length === 0}
      emptyMessage="No cheques found."
      tableAriaLabel="Cheque directory"
      wrapClassName="ub-table-wrap"
      tableClassName="ub-table"
    >
      {showRows
        ? cheques.map((row) => {
            const locked = isRowLocked?.(row) ?? false;
            return (
            <tr key={row.id}>
              {visibleDataColumns.map((col) => (
                <td key={col.id}>
                  {col.id === "status_name" ? (
                    <GlobalBadge
                      active={String(row.status_name || "")
                        .toLowerCase()
                        .includes("clear")}
                      activeText={row.status_name || "—"}
                      inactiveText={row.status_name || "—"}
                    />
                  ) : (
                    chequeCellValue(col.id, row)
                  )}
                </td>
              ))}
              <td className="ub-table__col-actions">
                <div className="ub-table-actions">
                  <GlobalTableActionButton action="view" onClick={() => onView?.(row.id)} />
                  {!locked ? (
                    <GlobalTableActionButton action="edit" onClick={() => onEdit?.(row.id)} />
                  ) : null}
                  {!locked ? (
                    <GlobalTableActionButton action="status" onClick={() => onChangeStatus?.(row)} />
                  ) : null}
                  <GlobalTableActionButton action="print" onClick={() => printChequeDetails(row)} />
                </div>
              </td>
            </tr>
          );
          })
        : null}
    </GlobalDirectoryGridSection>
  );
}

export default function ChequeDirectorySection({
  cheques,
  loading,
  filters,
  onFilterChange,
  masterData,
  paginationMeta,
  onPageChange,
  onView,
  onEdit,
  onChangeStatus,
  columnVisibility,
  onColumnVisibilityChange,
  canExport = true,
  isRowLocked,
}) {
  const inferred = useMemo(() => inferDirectoryFilterUi(filters), [filters]);
  const [filterColumn, setFilterColumn] = useState(inferred.column);
  const [filterValue, setFilterValue] = useState(inferred.value);

  const valueFilterOptions = useMemo(
    () => buildDirectoryValueOptions(filterColumn, masterData, cheques),
    [filterColumn, masterData, cheques],
  );

  const getExportData = () =>
    cheques.map((row) => {
      const out = {};
      for (const col of CHEQUE_DIRECTORY_COLUMN_DEFS) {
        out[col.label] = chequeCellValue(col.id, row);
      }
      return out;
    });

  const handleColumnChange = (column) => {
    setFilterColumn(column);
    setFilterValue("");
    onFilterChange(applyDirectoryColumnFilter(filters, "", ""));
  };

  const handleValueChange = (value) => {
    setFilterValue(value);
    onFilterChange(applyDirectoryColumnFilter(filters, filterColumn, value));
  };

  const handleClearFilters = () => {
    setFilterColumn("");
    setFilterValue("");
    onFilterChange({
      ...filters,
      search: "",
      bank_id: "",
      cheque_status_id: "",
      cheque_type_id: "",
      start_date: "",
      end_date: "",
      page: 1,
    });
  };

  return (
    <div className="ochq-panel" style={{ scrollMarginTop: "2rem" }}>
      <div className="ochq-panel__header">
        <h2 className="ochq-panel__title">Cheque directory</h2>
        <p className="user-builder-panel__hint">
          {canExport
            ? "Search and filter cheques, then view, edit, change status, or print details."
            : "Search and filter cheques. You can view, update status, and print. Edit is not available for cleared or cancelled cheques."}
        </p>
      </div>

      <GlobalDirectoryShell
        filters={
          <GlobalDirectoryFiltersSection
            searchLabel="Quick search"
            searchPlaceholder="Issue code, cheque no., payee, voucher…"
            searchValue={filters.search || ""}
            onSearchChange={(v) =>
              onFilterChange({ ...filters, search: v, page: 1 })
            }
            columnLabel="Filter by"
            columnValue={filterColumn}
            onColumnChange={handleColumnChange}
            gridColumnDefs={DIRECTORY_FILTER_COLUMNS}
            columnPlaceholderOption="Select"
            valueLabel="Value"
            valueValue={filterValue}
            onValueChange={handleValueChange}
            valueOptions={valueFilterOptions}
            valuePlaceholderOption="All"
            valueSelectDisabled={!filterColumn}
            onClear={handleClearFilters}
            clearFilterLabel="Clear filters"
            secondaryStart={
              <GlobalDirectoryColumnPicker
                columnDefs={CHEQUE_DIRECTORY_COLUMN_DEFS}
                visibility={columnVisibility}
                onVisibilityChange={onColumnVisibilityChange}
                storageKey={CHEQUE_DIRECTORY_COLUMN_VISIBILITY_KEY}
              />
            }
            secondaryEnd={
              canExport ? (
                <GlobalDirectoryExportMenu
                  getExportData={getExportData}
                  filenameBase="cheque_directory"
                />
              ) : null
            }
          />
        }
        grid={
          <ChequeDirectoryTable
            cheques={cheques}
            loading={loading}
            columnVisibility={columnVisibility}
            onView={onView}
            onEdit={onEdit}
            onChangeStatus={onChangeStatus}
            isRowLocked={isRowLocked}
          />
        }
        pagination={
          <GlobalDirectoryPaginationSection
            page={paginationMeta.page}
            totalPages={paginationMeta.totalPages}
            total={paginationMeta.total}
            pageSize={paginationMeta.limit}
            onPageChange={onPageChange}
            ariaLabel="Cheque directory pagination"
          />
        }
      />
    </div>
  );
}
